/**
 * App-wide constants and configuration.
 *
 * Server URL is RUNTIME-configurable (entered on the Connect screen, persisted to
 * localStorage) rather than baked at build time. This lets one build point at any
 * self-hosted backend — essential for the native iOS/macOS app, which has no
 * useful origin of its own. VITE_API_URL is only a fallback/seed for the suggested
 * default.
 */

const SERVER_KEY = 'whimer-server-url'

// Build-time seed (used only to suggest a default when nothing is saved).
const ENV_API = (import.meta.env.VITE_API_URL as string | undefined) || ''

/** Normalize user input into a clean origin (scheme + host + port), https by default. */
export function normalizeServerUrl(input: string): string {
  let s = (input || '').trim()
  if (!s) return ''
  if (!/^https?:\/\//i.test(s)) s = 'https://' + s // assume https when scheme omitted
  try {
    return new URL(s).origin // strips any path/query, normalizes host:port
  } catch {
    return ''
  }
}

/**
 * Suggested default when nothing is saved:
 *   - web / PWA: the current origin already serves the API (serve.cjs proxies it)
 *   - native app (capacitor://…): fall back to the build-time VITE_API_URL
 */
export function defaultServerUrl(): string {
  if (typeof window !== 'undefined' && /^https?:$/.test(window.location.protocol)) {
    return window.location.origin
  }
  return ENV_API ? normalizeServerUrl(ENV_API) : ''
}

/** Whether the user has explicitly saved a server URL. */
export function hasServerUrl(): boolean {
  try { return !!localStorage.getItem(SERVER_KEY) } catch { return false }
}

/** Configured server origin: saved value wins, else the suggested default. */
export function getServerUrl(): string {
  try {
    const saved = localStorage.getItem(SERVER_KEY)
    if (saved) return saved
  } catch { /* ignore */ }
  return defaultServerUrl()
}

export function setServerUrl(url: string): void {
  const norm = normalizeServerUrl(url)
  if (norm) {
    try { localStorage.setItem(SERVER_KEY, norm) } catch { /* private mode */ }
  }
}

export function clearServerUrl(): void {
  try { localStorage.removeItem(SERVER_KEY) } catch { /* ignore */ }
}

/** REST base — the server origin (API paths already include /api, /auth, /health). */
export function getApiUrl(): string {
  return getServerUrl()
}

/** WebSocket base — server origin with ws/wss derived from the http(s) scheme. */
export function getWsUrl(): string {
  const origin = getServerUrl()
  if (!origin) return ''
  return origin.replace(/^http/, 'ws') + '/ws' // http→ws, https→wss
}

// ─── Mobile detection ──────────────────────────────────────────────────────────
//
// Used to switch between voice-first layout (mobile) and keyboard-first (desktop).
// Not stored in state because user agent doesn't change during a session.

export const IS_MOBILE: boolean =
  typeof navigator !== 'undefined' &&
  /iPhone|iPad|Android/i.test(navigator.userAgent)

// ─── Terminal theme (GitHub Dark) ─────────────────────────────────────────────

export const TERMINAL_THEME = {
  background:          '#0D1117',
  foreground:          '#E6EDF3',
  cursor:              '#58A6FF',
  cursorAccent:        '#0D1117',
  selectionBackground: 'rgba(88, 166, 255, 0.3)',
  black:               '#484F58',
  red:                 '#FF7B72',
  green:               '#3FB950',
  yellow:              '#D29922',
  blue:                '#58A6FF',
  magenta:             '#BC8CFF',
  cyan:                '#39C5CF',
  white:               '#B1BAC4',
  brightBlack:         '#6E7681',
  brightRed:           '#FFA198',
  brightGreen:         '#56D364',
  brightYellow:        '#E3B341',
  brightBlue:          '#79C0FF',
  brightMagenta:       '#D2A8FF',
  brightCyan:          '#56D4DD',
  brightWhite:         '#F0F6FC',
} as const

// ─── Terminal font settings ────────────────────────────────────────────────────

// Latin renders in JetBrains Mono (true monospace) so ASCII / TUI boxes / numbers
// stay on the grid and xterm measures the cell from a fixed-width face. Thai has no
// glyph in JetBrains Mono, so it falls through to 'Sarabun' — the SAME Thai face the
// chat composer uses (var(--font)), loaded via Google Fonts. Sarabun is proportional,
// so Thai reads naturally (matching the input box) at the cost of perfect column
// alignment for mixed Thai/ASCII art. addon-unicode11 keeps the cursor math correct.
export const TERMINAL_FONT_FAMILY =
  "'JetBrains Mono', 'Sarabun', 'Noto Sans Mono', monospace"

// Slightly smaller on mobile to fit more content before the keyboard covers it
export const TERMINAL_FONT_SIZE = IS_MOBILE ? 13 : 14

// ─── Voice config ─────────────────────────────────────────────────────────────

// Silence duration before auto-submitting transcript
export const VOICE_SILENCE_TIMEOUT_MS = 1500
